import React, { useState } from 'react'
import { Box, Typography, CardMedia, ImageList, ImageListItem, IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { formatUnixTimeStampToDate } from '~/src/libs'
import { RequestOrderMessage } from '~/src/repositories/Order/models/Order'
import { convertContactOrderStatusTextToText } from '~/src/utils/convert'
import { useTranslation } from 'react-i18next'

interface IMessageListProps {
  id: string
  messages: RequestOrderMessage[]
  isLoading: boolean
  isShowAll?: boolean
  onDelete: (commentId: number, title: string, message: string, buttonTextOk: string, buttonTextNo: string, okButtonColor: string) => void
  isModal?: boolean
}

const MessageCard: React.FC<IMessageListProps> = ({
  id,
  messages,
  isLoading,
  isShowAll = true,
  onDelete,
  isModal
}) => {
  const { t } = useTranslation()
  if (isLoading) {
    return (
      <Typography ml={2} mt={2}>
        Loading...
      </Typography>
    )
  }

  if (!messages.length) {
    return (
      <Typography ml={2} mt={2}>
        {t('contact_order_detail.there_are_no_messages_yet')}
      </Typography>
    )
  }

  let displayedMessages = messages
  if (!isShowAll) {
    displayedMessages = messages.slice(0,4)
  }

  return (
    <>
      {displayedMessages.map((item, index) => {
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              borderBottom: '1px solid #EEEEEE',
              padding: '24px',
            }}
          >
            <CardMedia
              component="img"
              height="194"
              image={item.imageUser}
              alt={item.alt}
              sx={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
              }}
            />
            <Box ml={2.5}>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '18.2px',
                  color: '#222222',
                }}
              >
                {item.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '18.2px',
                  color: '#666666',
                  marginBottom: '12px',
                }}
              >
                {item.time && formatUnixTimeStampToDate(Number(item.time), 'yyyy/MM/dd h:mm')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontStyle: 'regular',
                  fontSize: '14px',
                  lineHeight: '18.2px',
                  color: '#222222',
                  marginBottom: '20px',
                }}
              >
                {item.description}
              </Typography>
              {item?.statusChangedFrom && item?.statusChangedTo &&
                <Typography
                  sx={{
                    bgcolor: '#F7F7F7',
                    fontFamily: 'Roboto',
                    fontStyle: 'regular',
                    fontSize: '14px',
                    lineHeight: '18.2px',
                    color: '#222222',
                    marginBottom: '20px',
                    padding:  '4px'
                  }}
                >
                  {t('contact_order_detail.status')}: {convertContactOrderStatusTextToText(item.statusChangedFrom)} &rarr; {convertContactOrderStatusTextToText(item.statusChangedTo)}
                </Typography>
              }
              {!!item?.imageComment?.length && (
                <ImageList>
                  {item?.imageComment?.map((item, index) => {
                    return (
                      item.src && (
                        <ImageListItem key={index}>
                          <a href={item.src} target="_blank" rel="noreferrer">
                            <Box
                              component="img"
                              sx={{ width: 80, height: 60, objectFit: 'cover' }}
                              src={item.src}
                              alt={item.alt}
                            />
                          </a>
                        </ImageListItem>
                      )
                    )
                  })}
                </ImageList>
              )}
            </Box>
            {!isModal && (
              <Box sx={{ marginLeft: 'auto' }}>
                {!!item.admin?.id && (
                <IconButton
                  color="primary"
                  onClick={() => onDelete(item.id, t('contact_order_detail.dialog_title'), t('contact_order_detail.dialog_message_admin'), t('contact_order_detail.dialog_ok'), t('contact_order_detail.dialog_ng'), 'error')}
                  sx={{
                    border: '1px solid rgba(63, 81, 181, 0.5)',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    marginLeft: '8px',
                    width: '36px',
                    height: '36px',
                  }}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
                </IconButton>
                )}
                {!item.admin?.id && (
                <IconButton
                  color="primary"
                  onClick={() => onDelete(item.id, t('contact_order_detail.dialog_title'), t('contact_order_detail.dialog_message_user'), t('contact_order_detail.dialog_ok'), t('contact_order_detail.dialog_ng'), 'error')}
                  sx={{
                    border: '1px solid rgba(63, 81, 181, 0.5)',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    marginLeft: '8px',
                    width: '36px',
                    height: '36px',
                  }}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
                </IconButton>
                )}
              </Box>
            )}
          </Box>
        )
      })}
    </>
  )
}

export default MessageCard
